import { ProductLine } from '@adc/ui-core'
import { user } from '@adc/blueprint'
import { NavigationMenuItem } from './types'
import {
  loggedInRoutes,
  generalRoutes,
  learnRoutes,
  aboutUs,
  buyGeneral,
  dealsForYou,
  getSellLinkData,
  getSmartSaleBuyLinkData,
} from '../constants/routes'
import { resolveUrl } from '../../../util/url'

// Flag key + parsing live in blueprint (single source of truth); re-exported
// here for the header's presentational render-gate.
export const DRIVE_DEMAND_TO_PRP_FLAG =
  user.prpIndicator.DRIVE_DEMAND_TO_PRP_FLAG

// Presentational gate for whether the deals-for-you nav item renders. The full
// flag config (on/off) is parsed in blueprint; here we only
// need the boolean.
export const isDealsForYouNavEnabled = (
  featureFlags: Record<string, unknown> = {}
): boolean => user.prpIndicator.parsePrpIndicatorFlag(featureFlags).enabled

// Link-visibility gate for the Deals For You direct link. Requires the nav to be
// enabled (flag) AND a known recommendation-set updated date that falls within
// the configment-driven staleness window (getPrpNavMaxAgeDays, default 30d).
// A missing date (SSR / not yet loaded / no recommendations) counts as NOT
// shown, so a stale or not-yet-known link never flashes in — the link only
// appears once we've confirmed a fresh date.
export const shouldShowDealsForYouLink = (
  featureFlags: Record<string, unknown> = {},
  dealsForYouUpdatedDate?: string | null
): boolean =>
  isDealsForYouNavEnabled(featureFlags) &&
  dealsForYouUpdatedDate != null &&
  user.prpIndicator.isPrpCategoryUpdatedRecently(
    dealsForYouUpdatedDate,
    user.prpIndicator.getPrpNavMaxAgeDays()
  )

// Normalized link name for a menu item, matching the convention used for the
// `linkName` property on the `navigation_click` event. A few ids map to
// bespoke labels; everything else falls back to the lowercased title, then id.
const MENU_ITEM_LINK_NAME_MAP: Record<string, string> = {
  'my-listings': 'my listings',
  'sell-with-us': 'my listing',
}

export const getMenuItemLinkName = (item: NavigationMenuItem): string =>
  MENU_ITEM_LINK_NAME_MAP[item.id] || item.title?.toLowerCase() || item.id

// The set of nav links currently shown, in render order. Dividers and
// logo-only items carry no link name and are excluded. Sent as
// `visibleDropdownLinks` on the `navigation_click` event so we can correlate a
// click against which links (e.g. "deals for you") were present at the time.
export const getVisibleMenuLinks = (
  menuItems: NavigationMenuItem[]
): string[] =>
  menuItems
    .filter(
      item => !item.isDivider && !item.isSmartSaleLogo && !item.isAuctionLogo
    )
    .map(getMenuItemLinkName)

// Flag that gates the Your Feed dashboard experience. When ON (and the user
// is logged in, outside SmartSale), the dropdown renders the redesigned menu
// (MERCH-173): DASHBOARD section with a featured Your Feed card and deep
// links, replacing the legacy item list.
export const FEED_NAV_FLAG = 'fe.resi.enable-dashboard-feed'

export const isFeedNavEnabled = (
  featureFlags: Record<string, unknown> = {}
): boolean => Boolean(featureFlags?.[FEED_NAV_FLAG])

interface MenuHandlers {
  onSignOut?: (e: React.MouseEvent) => void
  onNotifications?: (e: React.MouseEvent) => void
  onLogin?: (e: React.MouseEvent) => void
  onSignUp?: (e: React.MouseEvent) => void
}

export const getNavigationMenuItems = (
  featureFlags: Record<string, any> = {},
  handlers: MenuHandlers = {},
  isLoggedIn: boolean = true,
  theme?: any,
  options: {
    user?: any
    isMobile?: boolean
    showDealsForYouIndicator?: boolean
    dealsForYouUpdatedDate?: string | null
    baseUrl?: string
  } = {}
): NavigationMenuItem[] => {
  const {
    user,
    isMobile = false,
    showDealsForYouIndicator = false,
    dealsForYouUpdatedDate,
    baseUrl = '',
  } = options
  const sellLinkData = getSellLinkData(theme, featureFlags, baseUrl)
  const isSmartSale = theme?.productLine === ProductLine.SmartSale
  const hasListings =
    (user?.bidder_signature?.find(
      (sig: any) => sig.__typename === 'SellerSignature'
    )?.historical?.listing_count || 0) > 0
  // Prefix root-relative urls with baseUrl so links resolve against the
  // canonical origin when the header renders on a reverse-proxied host.
  const withBaseUrl = (items: NavigationMenuItem[]): NavigationMenuItem[] =>
    baseUrl
      ? items.map(item =>
          item.url ? { ...item, url: resolveUrl(item.url, baseUrl) } : item
        )
      : items
  const sellWithUsItem: NavigationMenuItem = {
    id: 'sell-with-us',
    title: 'Sell With Us',
    isSmartSaleLogo: true,
    url: sellLinkData.url,
    elmId: 'nav_sell_with_us',
  }

  const myListingsItem: NavigationMenuItem = {
    id: 'my-listings',
    title: 'My Listings',
    icon: 'sign',
    iconStyle: 'solid' as const,
    url: loggedInRoutes.myListings?.url || '/dashboard/seller/',
    elmId: 'nav_my_listings',
  }
  // SmartSale has a simplified menu
  if (isSmartSale) {
    const smartSaleBuyLink = getSmartSaleBuyLinkData()

    if (!isLoggedIn) {
      return withBaseUrl([
        {
          id: 'login',
          title: 'Log In',
          icon: 'arrow-right-to-bracket',
          iconStyle: 'solid',
          url: '#',
          elmId: 'nav_login',
          onClick: handlers.onLogin,
        },
        {
          id: 'signup',
          title: 'Sign Up',
          icon: 'user-plus',
          iconStyle: 'solid',
          url: '#',
          elmId: 'nav_signup',
          onClick: handlers.onSignUp,
        },
        {
          id: 'divider-1',
          isDivider: true,
          elmId: 'nav_divider_1',
          title: '',
        },
        {
          id: 'home',
          title: 'Home',
          icon: 'home',
          iconStyle: 'solid',
          url: '/',
          elmId: 'nav_home',
        },
        {
          id: 'buy',
          title: 'Buy',
          icon: 'magnifying-glass-location',
          iconStyle: 'solid',
          url: smartSaleBuyLink.url,
          elmId: 'nav_buy',
        },
        {
          id: 'sell',
          title: 'Sell',
          isSmartSaleLogo: true,
          url: sellLinkData.url,
          elmId: 'nav_sell',
        },
      ])
    }

    // SmartSale logged in menu
    return withBaseUrl([
      {
        id: 'home',
        title: 'Home',
        icon: 'home',
        iconStyle: 'solid',
        url: '/',
        elmId: 'nav_home',
      },
      {
        id: 'my-listings',
        title: 'My Listings',
        icon: 'sign',
        iconStyle: 'solid',
        url: loggedInRoutes.myListings?.url || '/dashboard/seller/',
        elmId: 'nav_my_listings',
      },
      {
        id: 'account',
        title: 'Account',
        icon: 'user',
        iconStyle: 'solid',
        url: loggedInRoutes.accountInfo?.url || '/dashboard/account-info/',
        elmId: 'nav_account',
      },
      {
        id: 'divider-1',
        isDivider: true,
        elmId: 'nav_divider_1',
        title: '',
      },
      {
        id: 'buy-with-us',
        title: 'Buy With Us',
        isAuctionLogo: true,
        url: smartSaleBuyLink.url,
        elmId: 'nav_buy_with_us',
      },
      {
        id: 'sell',
        title: 'Sell',
        isSmartSaleLogo: true,
        url: sellLinkData.url,
        elmId: 'nav_sell',
      },
      {
        id: 'divider-2',
        isDivider: true,
        elmId: 'nav_divider_2',
        title: '',
      },
      {
        id: 'sign-out',
        title: 'Sign Out',
        icon: 'door-open',
        iconStyle: 'solid',
        elmId: 'nav_sign_out',
        isHighlighted: true,
        className: 'sign-out-item',
        onClick: handlers.onSignOut,
      },
    ])
  }

  if (!isLoggedIn) {
    return withBaseUrl([
      {
        id: 'login',
        title: 'Log In',
        icon: 'arrow-right-to-bracket',
        iconStyle: 'solid',
        url: '#',
        elmId: 'nav_login',
        onClick: handlers.onLogin,
      },
      {
        id: 'signup',
        title: 'Sign Up',
        icon: 'user-plus',
        iconStyle: 'solid',
        url: '#',
        elmId: 'nav_signup',
        onClick: handlers.onSignUp,
      },
      {
        id: 'divider-1',
        isDivider: true,
        elmId: 'nav_divider_1',
        title: '',
      },
      {
        id: 'home',
        title: 'Home',
        icon: 'home',
        iconStyle: 'solid',
        url: '/',
        elmId: 'nav_home',
      },
      {
        id: 'divider-2',
        isDivider: true,
        elmId: 'nav_divider_2',
        title: '',
      },
      {
        id: 'buy',
        title: 'Buy',
        icon: 'magnifying-glass-location',
        iconStyle: 'solid',
        url: buyGeneral.url || '/residential',
        elmId: 'nav_buy',
      },
      {
        id: 'sell-with-us',
        title: 'Sell with us',
        isSmartSaleLogo: true,
        url: sellLinkData.url,
        elmId: 'nav_sell_with_us',
      },
      {
        id: 'learn',
        title: 'Learn',
        icon: 'chalkboard-user',
        iconStyle: 'solid',
        url: learnRoutes.helpCenter?.url || '/lp/learn-more/',
        elmId: 'nav_learn',
      },
      {
        id: 'blog',
        title: 'Blog',
        icon: 'message-pen',
        iconStyle: 'solid',
        url: learnRoutes.blog?.url || '/blog/',
        elmId: 'nav_blog',
      },
      {
        id: 'about-us',
        title: 'About Us',
        icon: 'users',
        iconStyle: 'solid',
        url: aboutUs.url || '/lp/about-us/',
        elmId: 'nav_about_us',
      },
    ])
  }

  // Wallet is flag-gated in every variant of the logged-in menu.
  const withWalletGate = (items: NavigationMenuItem[]): NavigationMenuItem[] =>
    items.filter(
      item =>
        item.id !== 'wallet' || Boolean(featureFlags?.['fe.resi.enable-wallet'])
    )

  // Redesigned menu behind the feed flag (MERCH-173). Web dropdown only —
  // the mobile drawer keeps the legacy menu until its own ticket.
  if (isFeedNavEnabled(featureFlags)) {
    const feedItems: NavigationMenuItem[] = [
      {
        id: 'dashboard-section',
        title: 'Dashboard',
        isSectionLabel: true,
        elmId: 'nav_dashboard_section',
      },
      {
        id: 'your-feed',
        title: 'Your Feed',
        icon: 'house-user',
        iconStyle: 'solid',
        isFeaturedCard: true,
        subtitle: 'Based on your buying criteria, saved searches, and activity',
        ctaLabel: "View today's deals",
        url: loggedInRoutes.yourFeed.url,
        elmId: 'nav_your_feed',
      },
      {
        id: 'saved-properties',
        title: 'Saved Properties',
        icon: 'heart',
        iconStyle: 'solid',
        url: loggedInRoutes.savedAssets?.url || '/dashboard/saved-properties/',
        elmId: 'nav_saved_properties',
      },
      {
        id: 'registered',
        title: 'Registered',
        icon: 'clipboard-check',
        iconStyle: 'solid',
        url: loggedInRoutes.registeredBids.url,
        elmId: 'nav_registered',
      },
      {
        id: 'remote-bids',
        title: 'Remote Bids',
        icon: 'gavel',
        iconStyle: 'solid',
        url: loggedInRoutes.remoteBids.url,
        elmId: 'nav_remote_bids',
      },
      {
        id: 'won-and-closed',
        title: 'Won & Closed',
        icon: 'trophy',
        iconStyle: 'solid',
        url: loggedInRoutes.wonAndClosed.url,
        elmId: 'nav_won_and_closed',
      },
      {
        id: 'divider-1',
        isDivider: true,
        elmId: 'nav_divider_1',
        title: '',
      },
      {
        id: 'buying-criteria',
        title: 'Buying Criteria',
        icon: 'location-crosshairs',
        iconStyle: 'solid',
        url: loggedInRoutes.userPreferences.url,
        elmId: 'nav_buying_criteria',
      },
      {
        id: 'saved-searches',
        title: 'Saved Searches',
        icon: 'bookmark',
        iconStyle: 'solid',
        url: '/dashboard/saved-searches/',
        elmId: 'nav_saved_searches',
      },
      {
        id: 'divider-2',
        isDivider: true,
        elmId: 'nav_divider_2',
        title: '',
      },
      // Figma copy: "Sell with Us" (legacy menu keeps "Sell With Us").
      ...(hasListings
        ? [myListingsItem]
        : [{ ...sellWithUsItem, title: 'Sell with Us' }]),
      {
        id: 'divider-3',
        isDivider: true,
        elmId: 'nav_divider_3',
        title: '',
      },
      {
        id: 'account',
        title: 'Account',
        icon: 'user',
        iconStyle: 'solid',
        url: loggedInRoutes.accountInfo?.url || '/dashboard/account-info/',
        elmId: 'nav_account',
      },
      {
        id: 'wallet',
        title: 'Wallet',
        icon: 'wallet',
        iconStyle: 'solid',
        url: loggedInRoutes.wallet?.url || '/dashboard/wallet/',
        elmId: 'nav_wallet',
      },
      {
        id: 'divider-4',
        isDivider: true,
        elmId: 'nav_divider_4',
        title: '',
      },
      {
        id: 'notifications',
        title: 'Notifications',
        icon: 'bell',
        iconStyle: 'solid',
        url: '/dashboard/notifications/',
        elmId: 'nav_notifications',
        onClick: handlers.onNotifications,
      },
      {
        id: 'learn',
        title: 'Learn',
        icon: 'chalkboard-user',
        iconStyle: 'solid',
        url: learnRoutes.helpCenter?.url || '/lp/learn-more/',
        elmId: 'nav_learn',
      },
      {
        id: 'divider-5',
        isDivider: true,
        elmId: 'nav_divider_5',
        title: '',
      },
      {
        id: 'sign-out',
        title: 'Sign Out',
        icon: 'door-open',
        iconStyle: 'solid',
        elmId: 'nav_sign_out',
        isHighlighted: true,
        className: 'sign-out-item',
        onClick: handlers.onSignOut,
      },
    ]

    return withBaseUrl(withWalletGate(feedItems))
  }

  const baseItems: NavigationMenuItem[] = [
    {
      id: 'notifications',
      title: 'Notifications',
      icon: 'bell',
      iconStyle: 'solid',
      url: '/dashboard/notifications/',
      elmId: 'nav_notifications',
      onClick: handlers.onNotifications,
    },
    ...(shouldShowDealsForYouLink(featureFlags, dealsForYouUpdatedDate)
      ? [
          {
            id: 'deals-for-you',
            title: dealsForYou.title,
            icon: 'gem',
            iconStyle: 'solid' as const,
            url: dealsForYou.url,
            elmId: dealsForYou.elmId,
            showIndicator: showDealsForYouIndicator,
          },
        ]
      : []),
    {
      id: 'preferences',
      title: 'Preferences',
      icon: 'folder-gear',
      iconStyle: 'solid',
      url: loggedInRoutes.userPreferences.url,
      elmId: 'nav_preferences',
    },
    {
      id: 'buyer-dashboard',
      title: 'Buyer Dashboard',
      icon: 'gavel',
      iconStyle: 'solid',
      url:
        loggedInRoutes.buyersDashboard?.url || '/dashboard/saved-properties/',
      elmId: 'nav_buyer_dashboard',
    },
    {
      id: 'saved-properties',
      title: 'Saved Properties',
      icon: 'heart',
      iconStyle: 'solid',
      url: loggedInRoutes.savedAssets?.url || '/dashboard/saved-properties/',
      elmId: 'nav_saved_properties',
    },
    {
      id: 'saved-searches',
      title: 'Saved Searches',
      icon: 'bookmark',
      iconStyle: 'solid',
      url: '/dashboard/saved-searches/',
      elmId: 'nav_saved_searches',
    },
    {
      id: 'divider-1',
      isDivider: true,
      elmId: 'nav_divider_1',
      title: '',
    },
    ...(hasListings ? [myListingsItem] : [sellWithUsItem]),
    {
      id: 'divider-2',
      isDivider: true,
      elmId: 'nav_divider_2',
      title: '',
    },
    {
      id: 'account',
      title: 'Account',
      icon: 'user',
      iconStyle: 'solid',
      url: loggedInRoutes.accountInfo?.url || '/dashboard/account-info/',
      elmId: 'nav_account',
    },
    {
      id: 'wallet',
      title: 'Wallet',
      icon: 'wallet',
      iconStyle: 'solid',
      url: loggedInRoutes.wallet?.url || '/dashboard/wallet/',
      elmId: 'nav_wallet',
    },
    ...(isMobile
      ? [
          {
            id: 'divider-3',
            isDivider: true,
            elmId: 'nav_divider_3',
            title: '',
          },
          {
            id: 'event-calendar',
            title: 'Event Calendar',
            icon: 'calendar',
            iconStyle: 'solid' as const,
            url: generalRoutes.calendar?.url || '/calendar',
            elmId: 'nav_event_calendar',
          },
          {
            id: 'learn',
            title: 'Learn',
            icon: 'chalkboard-user',
            iconStyle: 'solid' as const,
            url: learnRoutes.helpCenter?.url || '/lp/learn-more/',
            elmId: 'nav_learn',
          },
          {
            id: 'about-us',
            title: 'About Us',
            icon: 'users',
            iconStyle: 'solid' as const,
            url: aboutUs.url || '/lp/about-us/',
            elmId: 'nav_about_us',
          },
          ...(hasListings ? [sellWithUsItem] : []),
        ]
      : []),
    {
      id: 'divider-4',
      isDivider: true,
      elmId: 'nav_divider_4',
      title: '',
    },
    {
      id: 'sign-out',
      title: 'Sign Out',
      icon: 'door-open',
      iconStyle: 'solid',
      elmId: 'nav_sign_out',
      isHighlighted: true,
      className: 'sign-out-item',
      onClick: handlers.onSignOut,
    },
  ]

  return withBaseUrl(withWalletGate(baseItems))
}
